import { UserRole } from '../../auth/types/auth.types';
import type { TipoMiAlcance } from '../types/metas.types';

/**
 * ¿Se ve «Metas por colaborador» en el menú?
 *
 * - admin / superadmin: siempre, sin preguntar a la API.
 * - indicadores: sólo si `mi-alcance` le dio un tipo. Los 14 supervisores y los
 *   regionales conservan su rol `indicadores` y su tablero; lo que les abre
 *   este módulo es el ALCANCE asignado, no el rol. Sin alcance el item no
 *   aparece —mostrarlo sería enseñar una puerta que da a «no tienes tiendas»—.
 * - `undefined` = todavía no se sabe (o la consulta falló): oculto.
 *
 * La seguridad no está aquí: la API responde 403 a quien no tiene alcance.
 */
export function mostrarItemMetas(
  rol: UserRole | string | null | undefined,
  tipo: TipoMiAlcance | undefined,
): boolean {
  if (rol === UserRole.ADMIN || rol === UserRole.SUPERADMIN) return true;
  if (rol === UserRole.INDICADORES) return tipo !== null && tipo !== undefined;
  return false;
}

/** Sólo `indicadores` necesita preguntar a la API para decidir el menú. */
export const requiereConsultarAlcance = (rol: UserRole | string | null | undefined): boolean =>
  rol === UserRole.INDICADORES;

/**
 * Memoria del alcance en `sessionStorage`, por correo.
 *
 * El layout no pregunta `mi-alcance` en cada navegación: recuerda la respuesta.
 * Pero la memoria CADUCA: si un administrador le asigna el alcance a un
 * supervisor que ya tenía la pestaña abierta, sin caducidad el menú no le
 * aparecería hasta cerrar la pestaña, y lo primero que haría es llamar a TI.
 * Al caducar, el layout vuelve a preguntar en el siguiente cambio de ruta.
 */
export const MEMO_MENU_TTL_MS = 10 * 60 * 1000;

/**
 * «Sin alcance» (`null`) se recuerda poco: es justo el caso que cambia —la
 * cuenta a la que le acaban de asignar tiendas—, y recordarlo 10 minutos era
 * esconderle el menú 10 minutos después de la asignación. Un minuto basta para
 * no preguntar en cada clic del menú.
 */
export const MEMO_MENU_TTL_SIN_ALCANCE_MS = 60 * 1000;

/**
 * Si `mi-alcance` falla (red, API reiniciándose), el item de metas queda
 * oculto y no se guarda memoria: el siguiente cambio de ruta vuelve a
 * preguntar. Por si la persona no navega, el layout reintenta solo unas veces.
 */
export const REINTENTO_ALCANCE_MS = 30 * 1000;
export const MAX_REINTENTOS_ALCANCE = 3;

/** Vigencia de la memoria según lo que se recuerda. */
export const ttlMemoMenu = (tipo: TipoMiAlcance): number =>
  tipo === null ? MEMO_MENU_TTL_SIN_ALCANCE_MS : MEMO_MENU_TTL_MS;

export const llaveMemoMenu = (email: string): string =>
  `metas-colaborador.alcance:${email.trim().toLowerCase()}`;

interface MemoMenu {
  tipo: TipoMiAlcance;
  en: number;
}

type Almacen = Pick<Storage, 'getItem' | 'setItem'>;

/** `undefined` = no hay memoria vigente (hay que preguntar). */
export function leerMemoMenu(
  almacen: Almacen | null | undefined,
  email: string,
  ahora: number,
): TipoMiAlcance | undefined {
  if (!almacen || !email) return undefined;
  try {
    const crudo = almacen.getItem(llaveMemoMenu(email));
    if (!crudo) return undefined;
    const m = JSON.parse(crudo) as MemoMenu;
    // `direccion` desde el 21-oct-2026: sin él, la memoria de esa cuenta nunca valía
    // y el layout preguntaba `mi-alcance` en cada cambio de ruta.
    const validos: TipoMiAlcance[] = ['admin', 'supervisor', 'regional', 'direccion', 'mixto', null];
    if (typeof m?.en !== 'number' || !validos.includes(m.tipo)) return undefined;
    if (ahora - m.en > ttlMemoMenu(m.tipo) || ahora < m.en) return undefined;
    return m.tipo;
  } catch {
    return undefined;
  }
}

export function guardarMemoMenu(
  almacen: Almacen | null | undefined,
  email: string,
  tipo: TipoMiAlcance,
  ahora: number,
): void {
  if (!almacen || !email) return;
  try {
    almacen.setItem(llaveMemoMenu(email), JSON.stringify({ tipo, en: ahora } as MemoMenu));
  } catch {
    // Almacenamiento lleno o bloqueado (modo privado): sólo se pierde la memoria.
  }
}

/** `sessionStorage` si existe; en pruebas o en un navegador que lo bloquea, nada. */
export function almacenSesion(): Almacen | null {
  try {
    return typeof window !== 'undefined' && window.sessionStorage ? window.sessionStorage : null;
  } catch {
    return null;
  }
}

/**
 * Aviso de la pantalla al layout: «acabo de leer `mi-alcance`».
 *
 * La pantalla de metas lee el alcance cada vez que se abre; si le acaban de
 * asignar (o quitar) tiendas a la cuenta, el item del menú debe aparecer o
 * desaparecer YA, no cuando caduque la memoria. Un evento de `window` y no un
 * contexto: el layout y la pantalla no comparten más que eso, y así ninguno
 * importa al otro.
 */
export const EVENTO_ALCANCE_METAS = 'metas-colaborador:alcance';

export interface DetalleAlcanceMetas {
  email: string;
  tipo: TipoMiAlcance;
}

export function avisarAlcanceMetas(email: string, tipo: TipoMiAlcance): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(
    new CustomEvent<DetalleAlcanceMetas>(EVENTO_ALCANCE_METAS, { detail: { email, tipo } }),
  );
}

/** Se suscribe al aviso; devuelve la función para desuscribirse. */
export function escucharAlcanceMetas(cb: (d: DetalleAlcanceMetas) => void): () => void {
  if (typeof window === 'undefined') return () => undefined;
  const oyente = (e: Event) => {
    const d = (e as CustomEvent<DetalleAlcanceMetas>).detail;
    if (d) cb(d);
  };
  window.addEventListener(EVENTO_ALCANCE_METAS, oyente);
  return () => window.removeEventListener(EVENTO_ALCANCE_METAS, oyente);
}
